import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import {DataStatus} from 'platform/components';
import {Show} from 'platform/foundation';

import {isNotNil} from 'ramda';

import {useAssistantEntityContext} from '@dms/ai-assistant';
import {useGetCommentsQuery, useGetNoteQuery, useGetParticipationQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import {useAssistantSalesInterestContext} from '@dms/features/ai-assistant-interest';
import {CustomNoticeList, useCustomNoticesBadge} from '@dms/features/custom-notices';
import {TaskGridPage, useTasksCount} from '@dms/features/tasks';
import {TelephonyCallRecordVisit} from '@dms/features/telephony-call';
import i18n from '@dms/i18n';
import {interestRoutes, testIds} from '@dms/routes';
import {
  Checklists,
  CommunicationSection,
  getCommunicationSectionTitle,
  EMPTY_PLACEHOLDER,
  EntityHeader,
  NavigationItem,
  Page,
  Section,
  getCustomerName,
  useChecklist,
  useDocumentContextCount,
  queryParams,
  useCommunicationUnreadBadge,
  usePermissions,
} from '@dms/shared';

import {
  buildArray,
  composePath,
  DOT_CHARACTER,
  isNotNilOrEmpty,
  suffixTestId,
  useQueryState,
  useRequiredParams,
} from 'shared/web';

import {getInterestAssistantEntities} from '../../utils/getInterestAssistantEntities';
import {ActivityLog} from './(sections)/ActivityLog/ActivityLog';
import {Buying} from './(sections)/Buying/Buying';
import {Customer} from './(sections)/Customer/Customer';
import {Documents} from './(sections)/Documents/Documents';
import {Overview} from './(sections)/Overview/Overview';
import {Selling} from './(sections)/Selling/Selling';
import {SellingV2} from './(sections)/Selling/SellingV2';
import {InterestActivityPanel} from './components/InterestActivityPanel/InterestActivityPanel';
import {useInterestActions} from './hooks/useInterestActions';
import {useInterestData} from './hooks/useInterestData';
import {useInterestDropdownFlags} from './hooks/useInterestDropdownFlags';
import {useInterestFlags} from './hooks/useInterestFlags';
import {useInterestParameters} from './hooks/useInterestParameters';
import {useInterestSnippets} from './hooks/useInterestSnippets';

export function InterestDetail() {
  const {id: interestId} = useRequiredParams();
  const {ownerRef: assistantContextOwnerRef, synchronizeRef: synchronizeAssistantContextRef} =
    useAssistantSalesInterestContext({interestId});

  const {interest, customer, isLoading, isError} = useInterestData(interestId);
  const {ownerRef: assistantEntitiesOwnerRef, synchronizeRef: synchronizeAssistantEntitiesRef} =
    useAssistantEntityContext(getInterestAssistantEntities(interestId, interest));

  const snippets = useInterestSnippets(interestId);
  const flags = useInterestFlags(interestId);
  const dropdownFlags = useInterestDropdownFlags(interestId);
  const actions = useInterestActions(interestId);
  const {data: note} = useGetNoteQuery({
    resourceId: EntityResourceIds.interest,
    recordId: interestId,
    context: EntityResourceIds.interest,
  });
  const {data: comments = []} = useGetCommentsQuery({
    resourceId: EntityResourceIds.interest,
    recordId: interestId,
  });
  const parameters = useInterestParameters(interestId, comments.length > 0, note?.text);

  const {data: interestParticipation} = useGetParticipationQuery({
    recordId: interestId,
    resourceId: EntityResourceIds.interest,
  });

  const {badgeSettings, checklistSettings} = useChecklist({
    recordId: interestId,
    resourceId: 'INTEREST',
  });

  const {count: tasksTodoCount} = useTasksCount({
    entityId: interestId,
    relation: EntityResourceIds.interest,
    state: 'TODO',
  });
  const {count: tasksInProgressCount} = useTasksCount({
    entityId: interestId,
    relation: EntityResourceIds.interest,
    state: 'IN_PROGRESS',
  });
  const tasksCountBadge = tasksTodoCount + tasksInProgressCount;

  const {customNoticesBadge} = useCustomNoticesBadge({
    resourceId: EntityResourceIds.interest,
    recordId: interestId,
  });

  const documentsCountBadge = useDocumentContextCount('interest', interestId);
  const communicationUnreadBadge = useCommunicationUnreadBadge('INTEREST', interestId);
  // Set by the Kanban card's unread indicator; a one-time request, cleared once the thread scrolled.
  const [scrollToOldestUnread, , clearScrollToOldestUnread] = useQueryState(
    queryParams.COMMUNICATION_SCROLL_TO_OLDEST_UNREAD,
    true
  );

  const [
    canReadInterestTag,
    hasAddInterestTagPermission,
    hasUpdateInterestTagPermission,
    hasAddInterestTagToRecordPermission,
    hasRemoveInterestTagFromRecordPermission,
    hasRemoveInterestTagPermission,
    hasClearInterestTagExpirationPermission,
    hasSetInterestTagExpirationPermission,
  ] = usePermissions({
    permissionKeys: [
      'interestTagRead',
      'interestTagAdd',
      'interestTagUpdate',
      'interestTagAddToRecord',
      'interestTagRemoveFromRecord',
      'interestTagRemove',
      'interestTagClearExpiration',
      'interestTagSetExpiration',
    ],
    scopes: {
      interestTagAddToRecord: {
        participation: interestParticipation,
        branchId: interest?.branchId,
      },
      interestTagRemoveFromRecord: {
        participation: interestParticipation,
        branchId: interest?.branchId,
      },
      interestTagRead: {
        participation: interestParticipation,
        branchId: interest?.branchId,
      },
    },
  });

  const canCreateTag = hasAddInterestTagPermission && hasUpdateInterestTagPermission;

  const canAssignOrUnassignTag =
    hasAddInterestTagToRecordPermission && hasRemoveInterestTagFromRecordPermission;

  const canDeleteTag =
    hasRemoveInterestTagPermission &&
    hasClearInterestTagExpirationPermission &&
    hasSetInterestTagExpirationPermission;

  const maximization = {
    enabled: true,
    title: `${i18n.t('entity.interest.labels.interest')}: ${interest?.publicId ?? EMPTY_PLACEHOLDER}`,
  };

  const navigation = buildArray<NavigationItem>()
    .add({
      id: 'overview',
      label: i18n.t('entity.interest.labels.overviewTab'),
      href: composePath(interestRoutes.detailOverview, {params: {id: interestId}}),
      'data-testid': testIds.interest.detail('sectionItem-overview'),
      content: <Overview interestId={interestId} />,
    })
    .add({
      id: 'customer',
      label: i18n.t('entity.interest.labels.customerTab'),
      href: composePath(interestRoutes.detailCustomer, {params: {id: interestId}}),
      'data-testid': testIds.interest.detail('sectionItem-customer'),
      content: <Customer interestId={interestId} />,
    })
    .whenFeatureEnabled(featureFlags.SALES_TEST_DRIVES_V2, {
      id: 'selling',
      label: i18n.t('entity.interest.labels.sellingTab'),
      href: composePath(interestRoutes.detailSelling, {params: {id: interestId}}),
      'data-testid': testIds.interest.detail('sectionItem-selling'),
      content: <SellingV2 maximization={maximization} interestId={interestId} />,
    })
    .whenFeatureNotEnabled(featureFlags.SALES_TEST_DRIVES_V2, {
      id: 'selling',
      label: i18n.t('entity.interest.labels.sellingTab'),
      href: composePath(interestRoutes.detailSelling, {params: {id: interestId}}),
      'data-testid': testIds.interest.detail('sectionItem-selling'),
      content: <Selling maximization={maximization} interestId={interestId} />,
    })
    .add({
      id: 'buying',
      label: i18n.t('entity.interest.labels.buyingTab'),
      href: composePath(interestRoutes.detailBuying, {params: {id: interestId}}),
      'data-testid': testIds.interest.detail('sectionItem-buying'),
      content: <Buying interestId={interestId} />,
    })
    .add({
      id: 'communication',
      label: getCommunicationSectionTitle(),
      badge: communicationUnreadBadge,
      href: composePath(interestRoutes.detailCommunication, {params: {id: interestId}}),
      'data-testid': testIds.interest.detail('sectionItem-communication'),
      content: (
        <CommunicationSection
          maximization={maximization}
          customerId={interest?.customerId ?? ''}
          recordId={interestId}
          resourceId={EntityResourceIds.interest}
          scrollToOldestUnread={scrollToOldestUnread === 'true'}
          onScrollToOldestUnreadHandled={clearScrollToOldestUnread}
          data-testid="interest-communication"
        />
      ),
    })
    .add({
      id: 'documents',
      label: i18n.t('general.labels.documents'),
      href: composePath(interestRoutes.detailDocuments, {params: {id: interestId}}),
      'data-testid': testIds.interest.detail('sectionItem-documents'),
      content: <Documents maximization={maximization} interestId={interestId} />,
      badge: documentsCountBadge,
      hasSeparator: true,
    })
    .whenFeatureEnabled(featureFlags.SALE_TASKS_CHECKLIST, {
      id: 'checklists',
      label: i18n.t('entity.checklist.labels.title'),
      href: composePath(interestRoutes.detailChecklists, {params: {id: interestId}}),
      content: (
        <Section>
          <Checklists {...checklistSettings} />
        </Section>
      ),
      badge: badgeSettings,
      'data-testid': testIds.interest.detail('sectionItem-checklists'),
    })
    .add({
      id: 'tasks',
      label: i18n.t('entity.task.labels.tasks'),
      href: composePath(interestRoutes.detailTasks, {params: {id: interestId}}),
      'data-testid': testIds.interest.detail('sectionItem-tasks'),
      content: (
        <TaskGridPage
          maximization={maximization}
          gridCode="interest-task"
          entityId={interestId}
          data-testid={testIds.interest.detail('tasks')}
        />
      ),
      badge: {
        colorScheme: 'neutral',
        value: tasksCountBadge,
      },
    })
    .whenFeatureEnabled(featureFlags.CUSTOM_NOTICES, {
      id: 'customNotices',
      label: i18n.t('general.customNotices.labels.title'),
      href: composePath(interestRoutes.detailCustomNotices, {params: {id: interestId}}),
      content: (
        <CustomNoticeList
          resourceId={EntityResourceIds.interest}
          recordId={interestId}
          data-testid={testIds.interest.detail('customNotices')}
        />
      ),
      badge: customNoticesBadge,
      hasSeparator: true,
      'data-testid': testIds.interest.detail('sectionItem-customNotices'),
    })
    .add({
      id: 'activityLog',
      label: i18n.t('general.labels.activityLogs'),
      href: composePath(interestRoutes.detailActivityLog, {params: {id: interestId}}),
      'data-testid': testIds.interest.detail('sectionItem-activityLogs'),
      content: <ActivityLog maximization={maximization} />,
    });

  const detailTestId = suffixTestId('detail', {
    'data-testid': testIds.interest.detail('header'),
  });

  // How the after-call card names this interest: kind and type, then number and state.
  const callVisitTitle = [
    i18n.t('entity.customerMessage.labels.contextResource.INTEREST'),
    flags.find((flag) => !flag.isSubtle)?.label,
  ]
    .filter(isNotNilOrEmpty)
    .join(` ${DOT_CHARACTER} `);
  const callVisitDetails = [
    interest?.publicId,
    ...flags.filter((flag) => flag.isSubtle).map((flag) => flag.label),
  ];

  return (
    <>
      <div ref={assistantContextOwnerRef} hidden>
        <span ref={synchronizeAssistantContextRef} aria-hidden />
      </div>
      <div ref={assistantEntitiesOwnerRef} hidden>
        <span ref={synchronizeAssistantEntitiesRef} aria-hidden />
      </div>
      <Show when={isNotNil(customer)}>
        <Helmet title={i18n.t('page.interest.title', {name: getCustomerName(customer)})} />
      </Show>
      <TelephonyCallRecordVisit
        resourceId={EntityResourceIds.interest}
        recordId={interestId}
        title={callVisitTitle}
        details={callVisitDetails}
        customerName={getCustomerName(customer) ?? null}
      />
      <DataStatus key={interest?.id} isLoading={isLoading} isError={isError} minHeight={61}>
        <Page
          isFullHeight
          navigation={navigation}
          activityPanel={<InterestActivityPanel interestId={interestId} />}
          data-testid={detailTestId}
          header={
            <EntityHeader
              title={getCustomerName(customer) ?? EMPTY_PLACEHOLDER}
              icon="content/next_week"
              recordId={interestId}
              resourceId={EntityResourceIds.interest}
              controls={['ASSIGNEE', 'SOURCE', 'INTEREST_BRANCH']}
              enableTeams={isFeatureEnabled(featureFlags.CORE_TEAMS)}
              flags={flags}
              dropdownFlags={dropdownFlags}
              parameters={parameters}
              snippets={snippets}
              actions={actions}
              permissions={{
                tag: {
                  isDeletable: canDeleteTag,
                  isUpdatable: canCreateTag,
                  isReadable: canReadInterestTag,
                  isCreatable: canCreateTag,
                  isAssignable: canAssignOrUnassignTag,
                  isUnassignable: canAssignOrUnassignTag,
                },
              }}
              data-testid={detailTestId}
            />
          }
        />
      </DataStatus>
    </>
  );
}
